import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@/components/ui/accordion'

interface FaqSectionProps {
    faqs: Faq[]
}

const toJsonLd = (faqs: Faq[]): string => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    }

    return JSON.stringify(data).replace(/</g, '\\u003c')
}

export const FaqSection: FC<FaqSectionProps> = ({ faqs }): ReactNode => {
    if (faqs.length === 0) return null

    return (
        <section id='faq' className='border-t border-border/70 bg-secondary/50 py-20 lg:py-28'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-4'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionHeading
                            eyebrow="Questions and answers"
                            title="Good questions, honest answers"
                            description="Everything people usually ask us before they start. If yours is not here, ask us directly."
                        />

                        <Link
                            href='/contact'
                            className='mt-8 inline-flex items-center gap-1.5 font-semibold text-primary underline-offset-4 hover:underline'
                        >
                            Ask us a question
                            <ArrowRight className='size-4' />
                        </Link>
                    </div>
                </div>

                <div className='lg:col-span-8'>
                    <Accordion hiddenUntilFound className='border-t border-border'>
                        {faqs.map(faq => (
                            <AccordionItem key={faq.id} value={faq.id}>
                                <AccordionTrigger className='text-lg sm:text-xl'>
                                    <span className='font-heading font-medium' data-mdlfy-cms={`faqs:${faq.id}:question`}>{faq.question}</span>
                                </AccordionTrigger>

                                <AccordionContent>
                                    <p className='max-w-2xl leading-relaxed text-muted-foreground' data-mdlfy-cms={`faqs:${faq.id}:answer`}>{faq.answer}</p>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>

            <script
                type='application/ld+json'
                dangerouslySetInnerHTML={{ __html: toJsonLd(faqs) }}
            />
        </section>
    )
}
